/** Create a new external-ID mapping.
 *
 * Creation always makes an *alias* (`Additional_Ext_ID`): a primary ID is a
 * column on an entity that already exists, so "add a mapping" can only mean
 * adding an alias row to a tour.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import * as extIdsApi from '@/api/extIds';
import { ConsoleApiError } from '@/api/client';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { singlePageTenant } from '@/scope/pageTenants';

import { EMPTY_FORM, ExtIdFields, type ExtIdFormValue } from './ExtIdFields';
import { fieldErrorsOf } from './extIdErrors';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9): the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  /** Navigate to the new row's drawer once it exists. */
  onCreated?: (id: string) => void;
}

export function ExtIdCreateDialog({ open, onClose, pageTenants, onCreated }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant] = useState<TenantPickerValue | null>(() => singlePageTenant(pageTenants));
  const [form, setForm] = useState<ExtIdFormValue>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setTenant(singlePageTenant(pageTenants));
    setForm(EMPTY_FORM);
    setErrors({});
    // Seed on open only: `pageTenants` is a fresh array on every page render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tours, tags and fan-out targets are all tenant-scoped; keeping a stale
  // selection after a tenant change would submit another operator's ids.
  useEffect(() => {
    setForm((f) => ({ ...f, tour: null, tags: [], targets: [] }));
  }, [tenant?.id]);

  const mutation = useMutation({
    mutationFn: () =>
      extIdsApi.create({
        tour_operator_id: tenant!.id,
        tour_id: form.tour!.id,
        custom_id: form.custom_id,
        id_type: form.id_type,
        use_for_matching: form.use_for_matching,
        api_queryable: form.api_queryable,
        integration: form.integration.trim() || null,
        restech_account: form.restech_account.trim() || null,
        tag_ids: form.tags.map((t) => t.id),
        target_ids: form.targets.map((t) => t.id),
      }),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      snackbar.show('Mapping created.', 'success');
      onClose();
      onCreated?.(row.id);
    },
    onError: (e) => {
      const fields = fieldErrorsOf(e);
      setErrors(fields);
      if (Object.keys(fields).length === 0) {
        snackbar.show(
          e instanceof ConsoleApiError ? e.message : 'Could not create the mapping.',
          'error',
        );
      }
    },
  });

  const canSubmit = Boolean(tenant && form.tour && form.custom_id.trim()) && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New external ID mapping</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            A new mapping is always an <strong>additional</strong> ID on a tour. To change a
            tour&apos;s own external ID, edit that row instead.
          </Alert>
          <Box sx={{ maxWidth: 360 }}>
            <TenantPicker value={tenant} featured={pageTenants} onChange={setTenant} />
          </Box>
          <ExtIdFields
            value={form}
            onChange={setForm}
            tenantId={tenant?.id ?? null}
            kind="alias"
            errors={errors}
            disabled={mutation.isPending}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSubmit}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
